class AdaptiveImage extends HTMLElement {
  constructor () {
    super()
    // This list of asset widths to select from will be comprehensive for most use cases
    this.srcsetWidths = [493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946]

    const imageData = isBase64(this.dataset.image) ? atob(this.dataset.image) : this.dataset.image
    try {
      this.image = JSON.parse(imageData)
    } catch (e) {
      this.image = imageData
    }
    this.width = this.image?.width || 1946
    this.src = this.image?.src || this.image
    this.alt = this.dataset.alt || this.image?.alt
    this.sizes = this.dataset.sizes || '100vw'
    this.loading = this.dataset.loading || 'lazy'
    this.aspectRatio =
      this.image?.aspect_ratio
      || parseFloat(this.dataset.aspectRatio)
      || 1
    this.type = this.dataset.type || ''
    this.observeIntersection = this.dataset.observeIntersection === 'true' || false

    this.intersecting = false

    this.attachShadow({ mode: 'open' })
    this.render()
    this.handleObserveIntersection()
  }

  updateImage (data) {
    Object.assign(this, data)
    this.render()
  }

  generateStyles () {
    const aspectRatios = this.type == 'fit-guide'
      ? `aspect-ratio: ${this.aspectRatio};`
      : `padding-top: ${1 / this.aspectRatio * 100}%;`

    return `
      <style>
        :host {
          display: block;
          height: auto;
          position: relative;
          width: 100%;
          ${aspectRatios}
        }
        #image-element {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 8px;
        }
        @media (prefers-reduced-motion) {
          #image-element {
            transition: none !important;
          }
        }
      </style>
    `
  }

  generateSrcset () {
    let srcset = ''
    for (const width of this.srcsetWidths) {
      if (this.width >= width) srcset += `${this.src}&width=${width} ${width}w,`
    }
    srcset += `${this.src} ${this.width}w`
    return srcset
  }

  generateImageMarkup () {
    let srcset = this.srcset
    let src = this.src

    if (this.observeIntersection && !this.intersecting) {
      srcset = ''
      src = ''
    }

    return `
      <picture>
        <source srcset="${srcset}" sizes="${this.sizes}">
        <img id="image-element" src="${src}&width=${this.srcsetWidths[0]}w" alt="${this.alt}" loading="${this.loading}">
      </picture>
    `
  }

  handleObserveIntersection () {
    if (!this.observeIntersection) return

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.intersecting = true
          this.render()

          observer.unobserve(this)
        }
      })
    }, {rootMargin: '0px 0px 200px 0px'})

    observer.observe(this)
  }

  show () {
    this.style.display = 'block'
  }

  hide () {
    this.style.display = 'none'
  }

  render () {
    this.srcset = this.generateSrcset()
    this.shadowRoot.innerHTML = `
      ${this.generateStyles()}
      ${this.generateImageMarkup()}
    `
  }
}

window.customElements.define('adaptive-image', AdaptiveImage)
